import { useMemo, useState } from "react";

import { Input } from "@bigbinary/neeto-atoms";
import classNames from "classnames";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import Content from "./Content";
import { useFetchNeetoApps } from "./hooks/useNeetoApps";
import useProductSwitcherNavigation from "./hooks/useProductSwitcherNavigation";

const Menu = ({ isMobile = false }) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useFetchNeetoApps();

  const substring = searchTerm.replace(/ /g, "").toLowerCase();

  const filteredApps = useMemo(() => {
    if (!data) return [];

    return data?.neetoApps?.filter(({ name }) => {
      const nameLower = name.toLowerCase();
      const searchTermLower = substring.toLowerCase();

      const isNeetoPrefixMatch =
        searchTermLower.length <= 4 && "neeto".startsWith(searchTermLower);

      if (isNeetoPrefixMatch) return true;

      const productName = searchTermLower.startsWith("neeto")
        ? searchTermLower.replace("neeto", "")
        : searchTermLower;

      return nameLower.includes(productName);
    });
  }, [data, substring]);

  const { highlightedIndex, handleKeyDown, scrollContainerRef } =
    useProductSwitcherNavigation(filteredApps);

  return (
    <div
      className="bg-background flex w-full max-w-full flex-col items-start overflow-y-auto p-6 transition-all max-md:p-4"
      data-testid="switcher-wrapper"
      key="switcher-wrapper"
    >
      <div
        data-testid="product-switcher-body-wrapper"
        className={classNames(
          "mb-8 flex w-full items-center justify-between gap-8 pt-1 max-md:flex-col max-md:items-start max-md:gap-6",
          { "max-md:mb-6": isMobile }
        )}
      >
        {!isMobile && (
          <h1 className="text-2xl font-bold max-sm:text-xl">
            {t("neetoMolecules.productSwitcher.chooseNeetoProduct")}
          </h1>
        )}
        <div className="flex-grow lg:w-[296px] lg:flex-grow-0">
          <div className="relative">
            <Search className="text-muted-foreground absolute start-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              autoFocus
              className="h-10 ps-9"
              data-testid="product-switcher-search-input"
              placeholder={t("neetoMolecules.productSwitcher.searchProducts")}
              type="search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
      </div>
      <div
        className="mb-6 flex w-full flex-col gap-3 text-sm"
        ref={scrollContainerRef}
      >
        <Content {...{ filteredApps, highlightedIndex, isLoading }} />
      </div>
    </div>
  );
};

export default Menu;
